import { Link } from 'react-router-dom';
import Seo from '../components/Seo';
import PageHero from '../components/PageHero';
import Reveal from '../components/Reveal';
import SectionMarker from '../components/SectionMarker';
import HeartDivider from '../components/HeartDivider';
import BoardGrid from '../components/BoardGrid';
import { useAsync } from '../lib/useAsync';
import { membersApi } from '../api/resources';

// Area Directors timeline, newest biennium first.
// DUMMY DATA — replace each entry with the real Area Director, club, and biennium.
const AREA_DIRECTORS = [
  { term: '2026–2028', name: 'Azucena "Zeni" Yao', club: 'ZC Greater Rizal II' },
  { term: '2024–2026', name: 'Rowena Cruz-Plan', club: 'ZC Greater Rizal I' },
  { term: '2022–2024', name: 'Noni A. Pulhin', club: 'ZC Greater Rizal I' },
  { term: '2020–2022', name: 'Regina Padilla-Geraldez', club: 'ZC Manila I' },
];

const STRUCTURE = [
  { role: 'Area Director', desc: 'Leads the Area and coordinates member clubs.' },
  { role: 'Area Board', desc: 'Officers overseeing finance, programs, and membership.' },
  { role: 'Club Presidents', desc: 'Lead individual clubs and local initiatives.' },
  { role: 'Members', desc: 'The heart of Zonta — professionals driving change.' },
];

export default function About() {
  // Board members pulled live via the leadership filter; rest is scaffold content.
  const { data, loading, error, reload } = useAsync(
    () => membersApi.list({ leadership: 1, per_page: 20 }),
    []
  );
  // Area officers only: the board strip on About excludes club presidents
  // (leadership members whose position is "President"). Meet Area 1 Officers
  // & Presidents (/leadership) keeps the full roster.
  const officers = (data?.items || []).filter(
    (m) => (m.position || '').trim().toLowerCase() !== 'president'
  );

  return (
    <>
      <Seo title="About Us" description="Learn about the mission, leadership, and history of Zonta District 17 Area 1." />

      <PageHero
        eyebrow="Who We Are"
        title="A community of women changing the world"
        subtitle="United in our commitment to empower women through service and advocacy across District 17 Area 1."
        image="/img/community.jpg"
      />

      {/* Mission & Vision — asymmetric image + editorial text */}
      <section id="mission" className="section">
        <div className="container-page grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
          <div className="relative order-last lg:order-first">
            <div
              aria-hidden="true"
              className="absolute -bottom-5 -left-5 h-28 w-28 rounded-2xl bg-gold-100"
            />
            <div
              aria-hidden="true"
              className="absolute -right-5 -top-5 h-24 w-24 rounded-full border-[6px] border-maroon-100"
            />
            <div className="arch-sm relative shadow-card">
              <img
                src="/img/unity-hands.jpg"
                alt="Members joining hands in a circle, a symbol of unity and shared purpose."
                loading="lazy"
                className="aspect-[4/5] w-full object-cover sm:aspect-[5/4]"
              />
            </div>
          </div>

          <Reveal>
            <SectionMarker num="01" label="Mission & Vision" className="mb-6" />
            <h2 className="heading-brand text-3xl leading-tight sm:text-4xl">
              Building a better world for women and girls
            </h2>
            <div className="mt-7 space-y-7">
              <div>
                <h3 className="heading-brand text-xl">Our Mission</h3>
                <p className="mt-2 text-ink-soft">
                  Zonta International is a leading global organization of individuals working together to{' '}
                  <strong className="font-semibold text-ink">build a better world for women and girls</strong>.
                </p>
              </div>
              <div className="rounded-2xl bg-gold-50 p-5 ring-1 ring-gold-200">
                <span className="pill-gold mb-2">Our Vision</span>
                <div className="space-y-3 text-ink-soft">
                  <p>
                    Zonta International envisions a world in which women’s rights are recognized as human
                    rights and every woman is able to achieve her full potential.
                  </p>
                  <p>
                    In such a world, women have access to all resources and are represented in
                    decision-making positions on an equal basis with men.
                  </p>
                  <p>In such a world, no woman lives in fear of violence.</p>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Objectives strip */}
      <section className="bg-maroon-900 text-cream-100">
        <div className="container-page py-16 sm:py-20">
          <Reveal className="mx-auto mb-10 flex max-w-2xl flex-col items-center text-center">
            <h2 className="font-serif text-3xl font-bold text-white sm:text-4xl">
              What we stand for
            </h2>
            <HeartDivider className="mt-5 justify-center text-gold-400" />
          </Reveal>
          <div className="grid gap-6 sm:grid-cols-3">
            {[
              { t: 'Advocacy', d: 'Championing gender equality and women’s rights at every level.' },
              { t: 'Education', d: 'Scholarships and programs that open doors for young women.' },
              { t: 'Service', d: 'Local projects with measurable, lasting community impact.' },
            ].map((o, i) => (
              <Reveal key={o.t} delay={i * 90} className="rounded-2xl bg-white/5 p-7 ring-1 ring-white/10">
                <span className="font-display text-3xl font-extrabold text-gold-400">
                  0{i + 1}
                </span>
                <h3 className="mt-3 font-display text-xl font-bold text-white">{o.t}</h3>
                <p className="mt-2 text-cream-200">{o.d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership / Board preview */}
      <section id="leadership" className="section">
        <div className="container-page">
          <Reveal className="mb-10 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <SectionMarker num="02" label="Our People" className="mb-5" />
              <h2 className="heading-brand text-3xl sm:text-4xl">Area officers</h2>
              <p className="mt-2 max-w-xl text-ink-muted">
                The Area board — directors, secretary, treasurer and committee chairs guiding
                Zonta District 17 Area 1.
              </p>
            </div>
            <Link to="/leadership" className="btn-outline shrink-0 self-start sm:self-auto">
              Meet the full board
            </Link>
          </Reveal>
          <BoardGrid members={officers} loading={loading} error={error} onRetry={reload} />
        </div>
      </section>

      {/* Clubs teaser — links to the full directory at /clubs.
          Keeps id="clubs" so legacy /about#clubs anchors still land here. */}
      <section id="clubs" className="bg-surface-alt section">
        <div className="container-page grid items-center gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:gap-16">
          <div className="relative">
            <div
              aria-hidden="true"
              className="absolute -bottom-5 -right-5 h-24 w-24 rounded-full border-[6px] border-gold-400"
            />
            <div className="arch-sm relative shadow-card">
              <img
                src="/img/give-heart.jpg"
                alt="Volunteers forming a heart with their hands, representing the spirit of our clubs."
                loading="lazy"
                className="aspect-[4/5] w-full object-cover sm:aspect-square"
              />
            </div>
          </div>
          <Reveal>
            <SectionMarker num="03" label="Member Clubs" className="mb-5" />
            <h2 className="heading-brand text-3xl sm:text-4xl">
              A network of vibrant clubs
            </h2>
            <p className="mt-3 max-w-xl text-ink-muted">
              Our area is made up of clubs across the region, each rooted in its local community and
              led by a dedicated president. Explore every club and the women leading them.
            </p>
            <Link to="/clubs" className="btn-primary mt-8 inline-flex">
              Explore member clubs
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Org structure + History timeline */}
      <section id="structure" className="section">
        <div className="container-page grid gap-14 lg:grid-cols-2 lg:gap-16">
          <div>
            <h2 className="heading-brand text-2xl sm:text-3xl">How we’re organized</h2>
            <div className="mt-7 space-y-3">
              {STRUCTURE.map((s, i) => (
                <div key={s.role} className="card flex items-start gap-4 p-5">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-maroon-700 font-display text-sm font-bold text-white">
                    {i + 1}
                  </div>
                  <div>
                    <p className="font-display font-semibold text-ink">{s.role}</p>
                    <p className="mt-0.5 text-sm text-ink-muted">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div id="history">
            <h2 className="heading-brand text-2xl sm:text-3xl">Area Directors through the years</h2>
            <p className="mt-2 text-ink-muted">
              The leaders who have guided Area 1, biennium by biennium.
            </p>
            <ol className="relative mt-7 pl-6">
              <span
                aria-hidden="true"
                className="absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-full bg-maroon-200"
              />
              {AREA_DIRECTORS.map((d) => (
                <li key={d.term} className="relative mb-8 last:mb-0">
                  <span className="absolute -left-[26px] mt-1.5 h-4 w-4 rounded-full bg-maroon-700 ring-4 ring-cream-100" />
                  <p className="font-display text-lg font-bold text-maroon-700">{d.term}</p>
                  <p className="font-display font-semibold text-ink">{d.name}</p>
                  <p className="text-sm text-ink-muted">{d.club}</p>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>
    </>
  );
}
